package org.multipaz.mdoc.issuersigned

import org.multipaz.cbor.Bstr
import org.multipaz.cbor.Cbor
import org.multipaz.cbor.CborMap
import org.multipaz.cbor.DataItem
import org.multipaz.cbor.Tagged
import org.multipaz.crypto.Algorithm
import org.multipaz.crypto.Crypto
import kotlinx.io.bytestring.ByteString
import org.multipaz.cbor.buildCborMap
import org.multipaz.validation.ValidationResult
import org.multipaz.validation.buildValidationResult

/**
 * A data structure representing `IssuerSignedItem` in ISO/IEC 18013-5:2021.
 *
 * This is stored as a [DataItem] to preserve the order since the result of [calculateDigest] depends on the
 * exact bytes generated by the issuer. Since ISO/IEC 18013-5:2021 Clause 8.1 puts requirements on integers
 * and lengths, the only variable thing is map order meaning it's sufficient to use a [DataItem] instead
 * of the exact bytes from the issuer.
 *
 * Individual components can be retrieved using the [digestId], [random], [dataElementIdentifier], and
 * [dataElementValue] properties.
 *
 * @property dataItem a [DataItem] which is a map with keys `digestID`, `random`, `elementIdentifier`, and `elementValue`
 * as defined in ISO/IEC 18013-5:2021.
 */
data class IssuerSignedItem(
    val dataItem: DataItem
) {
    /**
     * The Digest ID.
     */
    val digestId: Long
        get() = dataItem["digestID"].asNumber

    /**
     * Random value.
     */
    val random: ByteString
        get() = ByteString(dataItem["random"].asBstr)

    /**
     * Data element identifier.
     */
    val dataElementIdentifier: String
        get() = dataItem["elementIdentifier"].asTstr

    /**
     * Data element value.
     */
    val dataElementValue: DataItem
        get() = dataItem["elementValue"]

    /**
     * Validates the internal structure of this [IssuerSignedItem].
     *
     * @return a [ValidationResult] containing any errors or warnings.
     */
    fun validate(): ValidationResult = buildValidationResult {
        if (dataItem !is CborMap) {
            addError("IssuerSignedItem dataItem is not a CBOR map")
            return@buildValidationResult
        }
        val expectedKeys = setOf("digestID", "random", "elementIdentifier", "elementValue")
        val actualKeys = dataItem.asMap.keys.mapNotNull {
            try {
                it.asTstr
            } catch (_: Throwable) {
                null
            }
        }.toSet()

        if (!dataItem.hasKey("digestID")) {
            addError("IssuerSignedItem missing 'digestID'")
        } else {
            try {
                val dId = dataItem["digestID"].asNumber
                if (dId < 0) {
                    addError("IssuerSignedItem 'digestID' must be non-negative (was $dId)")
                }
            } catch (e: Throwable) {
                addError("IssuerSignedItem 'digestID' is not a number: ${e.message}")
            }
        }

        if (!dataItem.hasKey("random")) {
            addError("IssuerSignedItem missing 'random'")
        } else {
            try {
                val rand = dataItem["random"].asBstr
                if (rand.size < 16) {
                    addError("IssuerSignedItem 'random' must be at least 16 bytes (was ${rand.size} bytes)")
                }
            } catch (e: Throwable) {
                addError("IssuerSignedItem 'random' is not a byte string: ${e.message}")
            }
        }

        if (!dataItem.hasKey("elementIdentifier")) {
            addError("IssuerSignedItem missing 'elementIdentifier'")
        } else {
            try {
                val elemId = dataItem["elementIdentifier"].asTstr
                if (elemId.isEmpty()) {
                    addError("IssuerSignedItem 'elementIdentifier' cannot be empty")
                }
            } catch (e: Throwable) {
                addError("IssuerSignedItem 'elementIdentifier' is not a text string: ${e.message}")
            }
        }

        if (!dataItem.hasKey("elementValue")) {
            addError("IssuerSignedItem missing 'elementValue'")
        }

        val extraKeys = actualKeys - expectedKeys
        if (extraKeys.isNotEmpty()) {
            addWarning("IssuerSignedItem contains unexpected keys: ${extraKeys.joinToString(", ")}")
        }
    }

    /**
     * Calculates the digest of `IssuerSignedItemBytes`.
     *
     * @param algorithm the digest algorithm to use, e.g. [Algorithm.SHA256].
     */
    suspend fun calculateDigest(algorithm: Algorithm): ByteString {
        val encodeIssuerSignedItemBytes =
            Cbor.encode(Tagged(Tagged.ENCODED_CBOR, Bstr(Cbor.encode(dataItem))))
        return ByteString(Crypto.digest(algorithm, encodeIssuerSignedItemBytes))
    }

    companion object {
        /**
         * Constructs a [org.multipaz.mdoc.issuersigned.IssuerSignedItem] from values.
         *
         * @param digestId the digest ID.
         * @param random the random value.
         * @param dataElementIdentifier the data element name.
         * @param dataElementValue the data element value.
         * @return a [org.multipaz.mdoc.issuersigned.IssuerSignedItem].
         */
        fun fromValues(
            digestId: Long,
            random: ByteString,
            dataElementIdentifier: String,
            dataElementValue: DataItem
        ) = IssuerSignedItem(
            dataItem = buildCborMap {
                put("digestID", digestId)
                put("random", random.toByteArray())
                put("elementIdentifier", dataElementIdentifier)
                put("elementValue", dataElementValue)
            }
        )
    }
}
